import { getCurrencyFormatter } from "@redotech/money/util";
import { useRequiredContext } from "@redotech/react-util/context";
import { useLoad } from "@redotech/react-util/load";
import RedoLogo from "@redotech/redo-icons/redo-logo-cutout.svg";
import { RedoMerchantRpcClientContext } from "@redotech/redo-merchant-app-common/rpc-client";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useCallback, useMemo, useState } from "react";
import { useSetupGuide } from "../../provider/use-setup-guide";
import { getProductConfig } from "../../registry/product-registry";
import type { IntakeAnswers, Product } from "../../registry/types";
import { AnimatedLogos } from "./animated-logos";
import { DecryptedText } from "./decrypted-text";
import * as css from "./intake-modal.module.css";
import { IntakeOption } from "./intake-option";
import { allQuestionsAnswered, isAnswered } from "./intake-validation";

export interface IntakeModalProps {
  product: Product;
  /** Submit handler — provider should pipe to submitMerchantSetupIntake RPC. */
  onSubmit: (answers: IntakeAnswers) => Promise<void>;
  onClose?: () => void;
}

/** "welcome" | index of the current question (0-based). */
type Step = "welcome" | number;

// Custom intake hero illustrating the merchant onboarding moment.
// Same S3 bucket the rest of the onboarding-v2 assets live in.
const DEFAULT_HERO_IMAGE_URL =
  "https://redoapi-dev.s3.us-east-1.amazonaws.com/redo/static/onboarding-v2/merchant-onboarding-in-app.webp";

// Same testimonials shown on the login hero panel — kept in sync manually
// for now since login is in a separate package.
const HERO_TESTIMONIALS = [
  {
    brand: "Willows Boutique",
    quote:
      "Auto batching has made our fulfillment process incredibly efficient. We're saving so much time!",
  },
  {
    brand: "Primo Golf",
    quote:
      "The cost savings is just nuts. We pay for dozens of point solutions and it adds up. Having all of your products under one platform is just so much better, and cheaper!",
  },
  {
    brand: "Popflex",
    quote:
      "Redo has transformed our returns process! The seamless ShipHero integration and customizable flows have saved our team countless hours, while the shop on-site exchange option keeps our customers happy and engaged.",
  },
  {
    brand: "Redo merchant",
    quote:
      "We were able to launch faster, test ideas sooner, and give our team one place to manage the customer journey.",
  },
  {
    brand: "Local Boy",
    quote:
      "Redo's software has made everything faster. It has freed up half of my day to work on other tasks.",
  },
  {
    brand: "Batch",
    quote:
      "The packing slips, the labels, our entire fulfillment setup, it all looks perfect. This setup is electric.",
  },
  {
    brand: "Henly",
    quote:
      "Redo's AI SMS abandonment messages are performing 3X better than our regular messages - that's huge for our brand! Super easy to set up and manage both SMS and email on the same page. Everything is just what I need!",
  },
  {
    brand: "ASRV",
    quote:
      "We appreciate Redo's seamless Shopify integration, robust reporting, and the ability to manage everything from returns to order tracking in one place. The team's responsiveness and commitment to our success make Redo an invaluable partner.",
  },
  {
    brand: "GLD",
    quote:
      "Honestly, Redo has been a game-changer for our exchange rates. We were looking for a way to get more customers to swap items instead of just returning them, and Redo has delivered.",
  },
  {
    brand: "Cuts",
    quote:
      "The cost savings alone made the change a no-brainer, but on top of that, their service has been unmatched.",
  },
] as const;

/**
 * Multi-step intake modal:
 *   • "welcome": "Welcome to Reclaim" + last-12-months chargeback loss stat
 *   • One step per intake question, prompt rendered as the H1 with no
 *     subtitle and the option list filling the body.
 *
 * Button text follows the position: "Continue" on welcome, "Next" between
 * questions, "Get started" on the last question (or "Try again" after a
 * submit error).
 *
 * Non-dismissible (intake submit failure must not strand the user — show
 * inline error + retry, never close on escape/backdrop).
 */
export const IntakeModal = memo(function IntakeModal({
  product,
  onSubmit,
  onClose,
}: IntakeModalProps) {
  const config = getProductConfig(product);
  const merchantRpcClient = useRequiredContext(RedoMerchantRpcClientContext);
  const setupGuide = useSetupGuide();

  // Answers live on the provider so partial drafts survive the modal
  // unmounting + remounting when the merchant navigates away from the
  // product's routes (the host scopes modal rendering by route).
  const answers = setupGuide.intakeAnswers;
  const setAnswer = setupGuide.setIntakeAnswer;

  const [step, setStep] = useState<Step>("welcome");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Pull last-12-months dispute stats (same source the legacy insights
  // page uses) so we can show "you're losing $X to chargebacks every year".
  const statsLoad = useLoad(
    async (signal) => {
      if (!config?.intake.welcome.useDisputeStatsSubtitle) {
        return undefined;
      }
      try {
        const today = Temporal.Now.plainDateISO();
        const result = await merchantRpcClient.getDisputeDashboardStats({
          startDate: today.subtract({ months: 12 }),
          endDate: today,
        });
        if (signal.aborted) {
          return undefined;
        }
        return result;
      } catch {
        return undefined;
      }
    },
    [config?.intake.welcome.useDisputeStatsSubtitle, merchantRpcClient],
  );
  const stats = statsLoad.value;
  const currency = stats?.currency ?? "USD";
  const formatter = useMemo(() => getCurrencyFormatter(currency), [currency]);
  const annualLoss =
    stats && stats.totalCount > 0
      ? Math.max(0, stats.finalizedAmount - stats.wonAmount)
      : null;

  const questions = config?.intake.questions ?? [];
  const lastIndex = questions.length - 1;
  const allAnswered = !!config && allQuestionsAnswered(questions, answers);
  const CustomExperience = config?.intake.renderExperience;

  const currentQuestion =
    typeof step === "number" ? questions[step] : undefined;
  const currentAnswered =
    !!currentQuestion &&
    isAnswered(currentQuestion, answers[currentQuestion.id]);
  const isLastQuestion = typeof step === "number" && step === lastIndex;

  const handleContinue = useCallback(async () => {
    if (step === "welcome") {
      setStep(0);
      return;
    }
    if (!currentAnswered) {
      return;
    }
    if (!isLastQuestion) {
      setStep(step + 1);
      return;
    }
    if (!allAnswered) {
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await onSubmit(answers);
      // Provider's onSuccess refetches; intakeCompletedAt flips and the
      // modal naturally unmounts via the showIntake gate.
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Could not save your answers. Please try again.",
      );
      setSubmitting(false);
    }
  }, [step, currentAnswered, isLastQuestion, allAnswered, answers, onSubmit]);

  if (!config) {
    return null;
  }

  const continueDisabled =
    typeof step === "number" && (!currentAnswered || submitting);
  const continueText =
    step === "welcome"
      ? "Continue"
      : isLastQuestion
        ? error
          ? "Try again"
          : "Get started"
        : "Next";
  const dismissible = config.intake.dismissible === true;
  const heroImageUrl = config.intake.heroImageUrl ?? DEFAULT_HERO_IMAGE_URL;
  return (
    <RedoModal
      className={css.intakeModal}
      contentScrollable
      hideCloseButton={!dismissible}
      hideFooter
      hideHeader
      isOpen
      mainContentClassName={css.modalMainContent}
      modalSize="xl"
      muiClassName={css.backdropBlur}
      noPadding
      onModalCloseRequested={dismissible ? () => onClose?.() : swallowClose}
      title={config.displayName}
    >
      <Flex className={css.frame} dir="row">
        {/* Left: step-shell-style column (header strip + step inner) */}
        <div className={css.contentColumn}>
          {/* Thin product-header strip — equivalent to onboarding's
              ProductHeader, scaled for the modal context. */}
          <div className={css.productHeader}>
            <RedoLogo className={css.productHeaderIcon} />
            <span className={css.productHeaderName}>{config.displayName}</span>
          </div>

          {CustomExperience ? (
            <CustomExperience
              onClose={dismissible ? onClose : undefined}
              onSubmit={onSubmit}
              product={product}
            />
          ) : (
            <>
              <div className={css.stepInner}>
                {step === "welcome" ? (
                  <div className={css.welcomeBlock}>
                    <WelcomeContent
                      annualLoss={annualLoss}
                      formatter={formatter}
                      loading={statsLoad.pending}
                      subtitle={config.intake.welcome.subtitle}
                      title={config.intake.welcome.title}
                      useDisputeStatsSubtitle={
                        config.intake.welcome.useDisputeStatsSubtitle === true
                      }
                    />
                  </div>
                ) : currentQuestion ? (
                  <div className={css.questionsBlock}>
                    <h1 className={css.title}>
                      <DecryptedText
                        animateOn="view"
                        key={currentQuestion.id}
                        sequential
                        speed={40}
                        text={currentQuestion.prompt}
                      />
                    </h1>
                    <div
                      className={
                        currentQuestion.layout === "grid"
                          ? css.optionGrid
                          : css.optionList
                      }
                    >
                      {currentQuestion.options.map((option) => (
                        <IntakeOption
                          disabled={submitting}
                          isSelected={
                            answers[currentQuestion.id] === option.value
                          }
                          key={option.value}
                          layout={currentQuestion.layout ?? "list"}
                          onClick={() =>
                            setAnswer(currentQuestion.id, option.value)
                          }
                          option={option}
                        />
                      ))}
                      {error && isLastQuestion && (
                        <Text fontSize="sm" textColor="error">
                          {error}
                        </Text>
                      )}
                    </div>
                  </div>
                ) : null}
              </div>

              {/* Footer band — white strip at the bottom of the left content
                  column. Holds the continue button (bottom-right). */}
              <div className={css.columnFooter}>
                <button
                  aria-disabled={continueDisabled}
                  className={css.continueButton}
                  data-disabled={continueDisabled ? "true" : undefined}
                  onClick={continueDisabled ? undefined : handleContinue}
                  type="button"
                >
                  <span
                    className={css.continueText}
                    data-pending={submitting ? "true" : undefined}
                  >
                    {continueText}
                  </span>
                  {submitting && (
                    <span className={css.spinnerOverlay}>
                      <span className={css.spinner} />
                    </span>
                  )}
                </button>
              </div>
            </>
          )}
        </div>

        {/* Right: hero panel — image + gradient + testimonial/logos.
            Same recipe as merchant/login/app/src/login-hero-panel. */}
        <div className={css.imageWrapper}>
          <img alt="" aria-hidden className={css.image} src={heroImageUrl} />
          <div className={css.heroOverlay} />
          <HeroPanelContent />
        </div>
      </Flex>
    </RedoModal>
  );
});

const WelcomeContent = memo(function WelcomeContent({
  annualLoss,
  formatter,
  loading,
  subtitle,
  title,
  useDisputeStatsSubtitle,
}: {
  annualLoss: number | null;
  formatter: Intl.NumberFormat;
  loading: boolean;
  subtitle: string;
  title: string;
  useDisputeStatsSubtitle: boolean;
}) {
  if (loading) {
    return null;
  }
  const resolvedSubtitle =
    useDisputeStatsSubtitle && annualLoss != null && annualLoss > 0
      ? `Looks like you're currently losing ${formatter.format(annualLoss)} to chargebacks every year. Let's fix that.`
      : subtitle;
  return (
    <>
      <h1 className={css.title}>
        <DecryptedText animateOn="view" sequential speed={40} text={title} />
      </h1>
      <p className={css.subtitle}>{resolvedSubtitle}</p>
    </>
  );
});

const HeroPanelContent = memo(function HeroPanelContent() {
  const testimonial = useMemo(() => {
    const randomIndex = Math.floor(Math.random() * HERO_TESTIMONIALS.length);
    return HERO_TESTIMONIALS[randomIndex] ?? HERO_TESTIMONIALS[0];
  }, []);

  return (
    <div className={css.heroContent}>
      <div className={css.trustedBy}>
        <span className={css.trustedByLabel}>Trusted by leading brands</span>
        <AnimatedLogos />
      </div>
      <blockquote className={css.testimonial}>
        <p className={css.quote}>{`"${testimonial.quote}"`}</p>
        <footer className={css.attribution}>
          <span className={css.authorName}>{testimonial.brand}</span>
        </footer>
      </blockquote>
    </div>
  );
});

function swallowClose(): void {
  // Intentional no-op — non-dismissible.
}
